import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AppShell } from "@/components/AppShell";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { BotRegistrationDialog } from "@/components/BotRegistrationDialog";
import { UpcomingCalendarWidget } from "@/components/UpcomingCalendarWidget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, Bot, Check, Clock, ListTodo, Sparkles, Video, Calendar as CalIcon } from "lucide-react";
import { toast } from "sonner";

function StatCard({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <Icon className={`h-4 w-4 ${accent ? "text-primary" : "text-muted-foreground"}`} />
      </div>
      <div className="mt-3 font-display text-3xl font-bold tracking-tight">{value}</div>
      {sub && <div className="mt-2 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function MeetingRow({ m }) {
  const dt = new Date(m.scheduled_at);
  return (
    <Link
      to={`/app/meetings/${m.meeting_id}`}
      className="flex items-center justify-between rounded-xl border border-border bg-card p-4 transition-colors hover:bg-accent/30"
      data-testid={`meeting-row-${m.meeting_id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{m.title}</div>
        <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="font-mono">{dt.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
          <span>·</span>
          <span>{Math.round((m.duration_seconds || 0) / 60)} min</span>
          <span>·</span>
          <span className="capitalize">{m.platform}</span>
        </div>
      </div>
      <div className="flex -space-x-2">
        {(m.attendees || []).slice(0, 3).map((a, i) => (
          <InitialsAvatar key={i} name={a.name} size="xs" className="ring-2 ring-card" />
        ))}
      </div>
      <Badge variant="outline" className="ml-3 capitalize">{m.status}</Badge>
    </Link>
  );
}

export default function DashboardHome() {
  const { user, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [botUrl, setBotUrl] = useState("");
  const [botTitle, setBotTitle] = useState("");
  const [botPlatform, setBotPlatform] = useState("zoom");
  const [botPassword, setBotPassword] = useState("");
  const [urlValidation, setUrlValidation] = useState(null);
  const [botLoading, setBotLoading] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  const [regMeetingType, setRegMeetingType] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get("/dashboard/stats");
      setStats(res.data);
    } catch (e) {
      console.error("Failed to load dashboard stats", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  // Live URL validation (debounced) — shows warnings/info under the URL field
  useEffect(() => {
    if (!botUrl?.trim()) { setUrlValidation(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/meetings/validate-url", { meeting_url: botUrl });
        setUrlValidation(r.data);
        if (r.data?.platform && r.data.platform !== "unknown") {
          setBotPlatform(r.data.platform === "google_meet" ? "meet" : r.data.platform);
        }
      } catch (e) { setUrlValidation(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [botUrl]);

  const doDeployBot = async (extras = {}) => {
    setBotLoading(true);
    try {
      const res = await api.post("/meetings/send-bot", {
        title: botTitle,
        meeting_url: botUrl || null,
        platform: botPlatform,
        meeting_password: botPassword || undefined,
        ...extras,
      });
      const m = res.data.meeting;
      if (m.status === "failed") {
        toast.error(m.error_message || "Bot deployment failed");
      } else if (res.data.mode === "demo") {
        toast.success("Demo analysis in progress");
      } else {
        toast.success("Bot deployed — joining your meeting");
      }
      setBotTitle(""); setBotUrl(""); setBotPassword(""); setUrlValidation(null); setRegOpen(false);
      await loadStats();
      await refreshUser();
      const meetingId = m.meeting_id;
      const poll = setInterval(async () => {
        const r = await api.get(`/meetings/${meetingId}`);
        if (["ready", "failed"].includes(r.data.meeting.status)) {
          clearInterval(poll);
          loadStats();
          if (r.data.meeting.status === "ready") {
            toast.success(`"${r.data.meeting.title}" summary is ready`);
          } else if (r.data.meeting.status === "failed" && r.data.meeting.error_message) {
            toast.error(r.data.meeting.error_message);
          }
        }
      }, 8000);
      setTimeout(() => clearInterval(poll), 35 * 60 * 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to deploy bot");
    } finally {
      setBotLoading(false);
    }
  };

  const sendBot = async (e) => {
    e.preventDefault();
    if (!botTitle.trim()) {
      toast.error("Please enter a meeting title");
      return;
    }
    // Detect URL type — if registration required, open dialog first
    if (botUrl?.trim()) {
      try {
        const det = await api.post("/meetings/detect-url", { meeting_url: botUrl });
        if (det.data.requires_registration) {
          setRegMeetingType("registration_required");
          setRegOpen(true);
          return;
        }
      } catch (e) {
        console.warn("URL detection failed (non-fatal)", e);
      }
    }
    await doDeployBot({});
  };

  const hoursPct = stats ? Math.min(100, (stats.hours_used / stats.hours_limit) * 100) : 0;
  const onboardingDone = (stats?.recent_meetings?.length || 0) > 0;

  return (
    <AppShell>
      <div className="mx-auto max-w-7xl px-6 py-10 md:px-10">
        {/* Header */}
        <div className="mb-10 flex items-end justify-between">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
            </div>
            <h1 className="mt-2 font-display text-4xl font-bold tracking-tight">
              Hello, {user?.name?.split(" ")[0] || "there"}.
            </h1>
          </div>
        </div>

        {/* Quick send bot */}
        <div className="mb-10 rounded-2xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Bot className="h-4 w-4 text-primary" />
            <h2 className="font-display text-lg font-medium">Send bot to a meeting</h2>
          </div>
          <form onSubmit={sendBot} className="space-y-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr,1fr,140px,auto]">
              <Input
                placeholder="Meeting title"
                value={botTitle}
                onChange={(e) => setBotTitle(e.target.value)}
                data-testid="bot-title-input"
              />
              <Input
                placeholder="Meeting URL (optional)"
                value={botUrl}
                onChange={(e) => setBotUrl(e.target.value)}
                data-testid="bot-url-input"
              />
              <Select value={botPlatform} onValueChange={setBotPlatform}>
                <SelectTrigger data-testid="bot-platform-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="zoom">Zoom</SelectItem>
                  <SelectItem value="meet">Google Meet</SelectItem>
                  <SelectItem value="teams">Microsoft Teams</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" disabled={botLoading} className="rounded-lg" data-testid="send-bot-button">
                {botLoading ? "Sending…" : "Send bot"}
              </Button>
            </div>

            {/* URL validation feedback */}
            {urlValidation?.info?.map((msg, i) => (
              <div key={`info-${i}`} className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900 dark:border-sky-900/40 dark:bg-sky-950/30 dark:text-sky-200"
                   data-testid="url-info-banner">
                <span className="font-mono">i</span>
                <span>{msg}</span>
              </div>
            ))}
            {urlValidation?.warnings?.map((msg, i) => (
              <div key={`warn-${i}`} className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200"
                   data-testid="url-warning-banner">
                <span className="font-mono">!</span>
                <span>{msg}</span>
              </div>
            ))}

            {/* Optional password field (shown for Zoom standard meeting without ?pwd=) */}
            {urlValidation?.needs_password && (
              <Input
                type="text"
                placeholder="Meeting password (optional)"
                value={botPassword}
                onChange={(e) => setBotPassword(e.target.value)}
                data-testid="bot-password-input"
              />
            )}
          </form>
          <p className="mt-3 font-mono text-xs text-muted-foreground">
            With a real meeting URL (Zoom/Meet/Teams), Noteology sends a Recall.ai bot live.
            Without a URL, we run a sample-transcript demo through Claude.
          </p>
        </div>

        {/* Stats */}
        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Meetings this month"
              value={stats?.meetings_this_month || 0}
              sub="All-time captured"
              icon={Video}
            />
            <div className="rounded-xl border border-border bg-card p-5" data-testid="stat-hours-used">
              <div className="flex items-start justify-between">
                <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Hours used</div>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="mt-3 font-display text-3xl font-bold tracking-tight">
                {stats?.hours_used.toFixed(1)}<span className="text-base font-normal text-muted-foreground"> / {stats?.hours_limit}h</span>
              </div>
              <Progress value={hoursPct} className="mt-3 h-1.5" />
            </div>
            <StatCard
              label="Open action items"
              value={stats?.open_actions || 0}
              sub="across all meetings"
              icon={ListTodo}
              accent
            />
            <StatCard
              label="Completed"
              value={stats?.done_actions || 0}
              sub="follow-throughs"
              icon={Check}
            />
          </div>
        )}

        {/* Lists */}
        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-lg font-medium">Upcoming (next 7 days)</h2>
              <Link to="/app/settings?tab=calendar" className="font-mono text-xs uppercase text-muted-foreground hover:text-foreground">
                Calendar
              </Link>
            </div>
            <UpcomingCalendarWidget />
          </div>

          <div>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-lg font-medium">Recent</h2>
              <Link to="/app/meetings" className="font-mono text-xs uppercase text-muted-foreground hover:text-foreground">
                All meetings <ArrowRight className="inline h-3 w-3" />
              </Link>
            </div>
            <div className="flex flex-col gap-3" data-testid="recent-meetings-list">
              {(stats?.recent_meetings || []).length === 0 ? (
                <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                  No meetings analyzed yet. Send a bot above to see Noteology in action.
                </div>
              ) : (
                stats.recent_meetings.map((m) => <MeetingRow key={m.meeting_id} m={m} />)
              )}
            </div>
          </div>
        </div>

        {/* Onboarding */}
        {!onboardingDone && (
          <div className="mt-10 rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-4 w-4 text-primary" />
              <h2 className="font-display text-lg font-medium">Get started</h2>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <OnboardingStep done label="Account created" />
              <OnboardingStep label="Connect calendar" hint="Settings → Calendar" />
              <OnboardingStep label="Try sending a bot" hint="Use the form above" />
            </div>
          </div>
        )}
      </div>
      <BotRegistrationDialog
        open={regOpen}
        onOpenChange={setRegOpen}
        meetingType={regMeetingType}
        defaults={{
          bot_name: user?.bot_default_name || user?.bot_display_name || "Noteology AI",
          bot_email: user?.bot_default_email || "",
          bot_company: user?.bot_default_company || "",
        }}
        submitting={botLoading}
        onSubmit={(vals) => doDeployBot(vals)}
      />
    </AppShell>
  );
}

function OnboardingStep({ done, label, hint }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-background p-4">
      <div className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full border ${done ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
        {done && <Check className="h-3 w-3" />}
      </div>
      <div className="min-w-0">
        <div className={`text-sm font-medium ${done && "text-muted-foreground line-through"}`}>{label}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </div>
    </div>
  );
}
